import React, { useState, useCallback, useEffect } from 'react';

import { apiContainer } from '@vlabs/api-bindings';
import { InfoCircleIcon, ArrowInCircleRightIcon, DetectionIcon } from '@vlabs/icons';
import { EmptyCard } from '@vlabs/shared/components/empty-card';
import { EventInfoCell } from '@vlabs/shared/components/event-info-cell/EventInfoCell';
import { BaseSample } from '@vlabs/shared/components/sample/BaseSample';
import { SimilarityTag } from '@vlabs/shared/components/similarity-tag/SimilarityTag';
import { calcTagType } from '@vlabs/shared/utils';
import { CircleAnimatedButton, useWindowSize } from '@vlabs/uikit';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import st from './EventTopMatchCell.module.sass';

export const EventTopMatchCell = ({
  topMatch,
  originalDetections,
  onClick,
}) => {
  const { t } = useTranslation();
  const tagColor = calcTagType(topMatch.similarity);
  const windowSize = useWindowSize();

  const [event, setEvent] = useState({ ...topMatch, isExist: true });
  const fetchEvent = useCallback(async () => {
    try {
      const { data } = await apiContainer.lunaClient.events.get(topMatch?.event_id);
      setEvent({ ...data, ...event });
    } catch ({ error }) {
      // Если событие удалено - 'Object not found'
      if (error.response.data.error_code === 23001) {
        setEvent({ ...event, isExist: false });
        console.error('Event not found:', error);
      } else {
        throw error;
      }
    }
  }, [topMatch?.event_id]);

  const shouldShowFaceEmptyCard = originalDetections.hasFace
    && !event?.uiFaceDetection
    && (originalDetections.hasBody || !event?.uiBodyDetection);

  const shouldShowBodyEmptyCard = originalDetections.hasBody
    && !event?.uiBodyDetection
    && (originalDetections.hasFace || !event?.uiFaceDetection);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchEvent(); }, []);

  return (
    <div className={cn(st.Content, st[`Content_BorderColor_${tagColor}`])}>
      {event?.isExist && (
        <div>
          <div className={st.GoToButton}>
            <CircleAnimatedButton
              icon={<ArrowInCircleRightIcon />}
              onClick={onClick}
              size={windowSize.width < 1920 ? 'small' : 'medium'}
            >
              {t('events:page.title')}
            </CircleAnimatedButton>
          </div>
          {event?.uiFaceDetection && (
            <BaseSample
              data-testid="topMatchSample"
              dataTip={t('events:check_policy_settings')}
              imageUrl={event.uiFaceDetection?.uiSampleURL}
              type="face"
            />
          )}

          {shouldShowFaceEmptyCard && <EmptyCard className={st.EmptyFaceCard} text={t('no data.title')} />}

          {(event?.uiFaceDetection || shouldShowFaceEmptyCard)
          && (event?.uiBodyDetection || shouldShowBodyEmptyCard)
          && <span className={st.Divider} />}

          {event?.uiBodyDetection && (
            <BaseSample
              data-testid="topMatchSample"
              dataTip={t('events:check_policy_settings')}
              imageUrl={event.uiBodyDetection?.uiSampleURL}
              type="body"
            />
          )}
          {shouldShowBodyEmptyCard && <EmptyCard className={st.EmptyBodyCard} text={t('no data.title')} />}
        </div>
      )}
      <div className={st.InfoPanel}>
        <SimilarityTag icon={<DetectionIcon />} size={windowSize.width < 1920 ? 'medium' : 'large'} title={t('event')} value={event.similarity} />
        <EventInfoCell className={st.EventInfoCell} original={event} />
        {!event?.isExist && (
          <div className={st.DeletedEvent}>
            <InfoCircleIcon className="StaticIcon" />
            {t('events:event deleted')}
          </div>
        )}
      </div>
    </div>
  );
};

EventTopMatchCell.propTypes = {
  topMatch: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired,
  originalDetections: PropTypes.shape({
    hasBody: PropTypes.bool.isRequired,
    hasFace: PropTypes.bool.isRequired,
  }).isRequired,
};
